import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Plus, Edit, Trash2, Utensils, MapPin, Phone, Globe,
  Leaf, ChefHat, Clock, DollarSign, Truck, ShoppingBag,
  Eye, EyeOff, Star, Image as ImageIcon
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { seller } from '@/lib/api';
import { toast } from 'sonner';

const cuisineTypes = [
  'Turkish', 'Mediterranean', 'Italian', 'Asian', 'FastFood', 
  'Seafood', 'Vegetarian', 'Indian', 'Mexican', 'French'
];

const menuCategories = [
  { value: 'appetizer', label: 'Başlangıçlar' },
  { value: 'soup', label: 'Çorbalar' },
  { value: 'salad', label: 'Salatalar' },
  { value: 'main_course', label: 'Ana Yemekler' },
  { value: 'kebab', label: 'Kebaplar' },
  { value: 'pide', label: 'Pideler' },
  { value: 'dessert', label: 'Tatlılar' },
  { value: 'beverage', label: 'İçecekler' },
];

const RestaurantTab = () => {
  const { t } = useTranslation();
  const [restaurants, setRestaurants] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddRestaurant, setShowAddRestaurant] = useState(false);
  const [showAddMenuItem, setShowAddMenuItem] = useState(false);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [activeSubTab, setActiveSubTab] = useState('restaurants');
  
  const [newRestaurant, setNewRestaurant] = useState({
    name: '',
    description: '',
    cuisine_type: 'Turkish',
    country: 'Türkiye',
    city: '',
    address: '',
    phone: '',
    email: '',
    website: '',
    images: [],
    delivery_available: false,
    takeaway_available: true,
    dine_in_available: true,
    minimum_order: 0,
    delivery_fee: 0,
    price_range: '$$',
    features: []
  });
  
  const [newMenuItem, setNewMenuItem] = useState({
    name: '',
    description: '',
    price: 0,
    currency: 'TRY',
    category: 'main_course',
    image_url: '',
    is_vegetarian: false,
    is_vegan: false,
    is_halal: true,
    is_gluten_free: false,
    spicy_level: 0,
    preparation_time: 15
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [restaurantsRes, ordersRes] = await Promise.all([
        seller.restaurants().catch(() => ({ data: [] })),
        seller.restaurantOrders().catch(() => ({ data: [] }))
      ]);
      setRestaurants(restaurantsRes.data || []);
      setOrders(ordersRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRestaurant = async () => {
    try {
      await seller.createRestaurant(newRestaurant);
      toast.success(t('seller.restaurantCreated'));
      setShowAddRestaurant(false);
      setNewRestaurant({
        name: '', description: '', cuisine_type: 'Turkish', country: 'Türkiye',
        city: '', address: '', phone: '', email: '', website: '', images: [],
        delivery_available: false, takeaway_available: true, dine_in_available: true,
        minimum_order: 0, delivery_fee: 0, price_range: '$$', features: []
      });
      fetchData();
    } catch (error) {
      toast.error(t('seller.restaurantCreateFailed'));
    }
  };

  const handleAddMenuItem = async () => {
    if (!selectedRestaurant) return;
    try {
      await seller.addMenuItem(selectedRestaurant.id, newMenuItem);
      toast.success(t('seller.menuItemAdded'));
      setShowAddMenuItem(false);
      setNewMenuItem({
        name: '', description: '', price: 0, currency: 'TRY', category: 'main_course',
        image_url: '', is_vegetarian: false, is_vegan: false, is_halal: true,
        is_gluten_free: false, spicy_level: 0, preparation_time: 15
      });
      fetchData();
    } catch (error) {
      toast.error(t('seller.menuItemAddFailed'));
    }
  };

  const handleDeleteRestaurant = async (id) => {
    if (!confirm(t('seller.confirmDelete'))) return;
    try {
      await seller.deleteRestaurant(id);
      toast.success(t('seller.restaurantDeleted'));
      fetchData();
    } catch (error) {
      toast.error(t('seller.deleteFailed'));
    }
  };

  const handleDeleteMenuItem = async (restaurantId, itemId) => {
    if (!confirm(t('seller.confirmDelete'))) return;
    try {
      await seller.deleteMenuItem(restaurantId, itemId);
      toast.success(t('seller.menuItemDeleted'));
      fetchData();
    } catch (error) {
      toast.error(t('seller.deleteFailed'));
    }
  };

  const handleOrderStatusUpdate = async (orderId, status) => {
    try {
      await seller.updateRestaurantOrderStatus(orderId, status);
      toast.success(t('seller.statusUpdated'));
      fetchData();
    } catch (error) {
      toast.error(t('seller.statusUpdateFailed'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Sub-tabs */}
      <Tabs value={activeSubTab} onValueChange={setActiveSubTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="restaurants" className="flex items-center gap-2">
            <Utensils className="w-4 h-4" />
            {t('seller.myRestaurants')} ({restaurants.length})
          </TabsTrigger>
          <TabsTrigger value="menu" className="flex items-center gap-2">
            <ChefHat className="w-4 h-4" />
            {t('seller.menuManagement')}
          </TabsTrigger>
          <TabsTrigger value="orders" className="flex items-center gap-2">
            <ShoppingBag className="w-4 h-4" />
            {t('seller.orders')} ({orders.length})
          </TabsTrigger>
        </TabsList>

        {/* Restaurants Tab */}
        <TabsContent value="restaurants" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">{t('seller.myRestaurants')}</h3>
            <Button onClick={() => setShowAddRestaurant(true)}>
              <Plus className="w-4 h-4 mr-2" />
              {t('seller.addRestaurant')}
            </Button>
          </div>

          {restaurants.length === 0 ? (
            <Card className="p-8 text-center">
              <Utensils className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">{t('seller.noRestaurants')}</p>
              <Button className="mt-4" onClick={() => setShowAddRestaurant(true)}>
                {t('seller.addFirstRestaurant')}
              </Button>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {restaurants.map(restaurant => (
                <Card key={restaurant.id}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h4 className="font-bold text-lg">{restaurant.name}</h4>
                        <Badge>{restaurant.cuisine_type}</Badge>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="icon"
                          onClick={() => {
                            setSelectedRestaurant(restaurant);
                            setActiveSubTab('menu');
                          }}
                        >
                          <ChefHat className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="destructive" 
                          size="icon"
                          onClick={() => handleDeleteRestaurant(restaurant.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">{restaurant.description}</p>
                    <div className="flex items-center gap-2 text-sm">
                      <MapPin className="w-4 h-4" />
                      <span>{restaurant.city}, {restaurant.country}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm mt-1">
                      <Phone className="w-4 h-4" />
                      <span>{restaurant.phone}</span>
                    </div>
                    <div className="flex gap-2 mt-3">
                      {restaurant.delivery_available && <Badge variant="outline"><Truck className="w-3 h-3 mr-1" />{t('restaurant.delivery')}</Badge>}
                      {restaurant.takeaway_available && <Badge variant="outline"><ShoppingBag className="w-3 h-3 mr-1" />{t('restaurant.takeaway')}</Badge>}
                    </div>
                    <div className="mt-3 text-sm text-muted-foreground">
                      {restaurant.menu_items?.length || 0} {t('seller.menuItems')}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Menu Management Tab */}
        <TabsContent value="menu" className="space-y-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <h3 className="text-lg font-semibold">{t('seller.menuManagement')}</h3>
              {restaurants.length > 0 && (
                <Select 
                  value={selectedRestaurant?.id || ''} 
                  onValueChange={(v) => setSelectedRestaurant(restaurants.find(r => r.id === v))}
                >
                  <SelectTrigger className="w-64">
                    <SelectValue placeholder={t('seller.selectRestaurant')} />
                  </SelectTrigger>
                  <SelectContent>
                    {restaurants.map(r => (
                      <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            {selectedRestaurant && (
              <Button onClick={() => setShowAddMenuItem(true)}>
                <Plus className="w-4 h-4 mr-2" />
                {t('seller.addMenuItem')}
              </Button>
            )}
          </div>

          {!selectedRestaurant ? (
            <Card className="p-8 text-center">
              <ChefHat className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">{t('seller.selectRestaurantFirst')}</p>
            </Card>
          ) : (
            <div className="space-y-4">
              {menuCategories.map(category => {
                const items = selectedRestaurant.menu_items?.filter(item => item.category === category.value) || [];
                if (items.length === 0) return null;
                
                return (
                  <Card key={category.value}>
                    <CardHeader>
                      <CardTitle className="text-base">{category.label}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>{t('seller.itemName')}</TableHead>
                            <TableHead>{t('seller.description')}</TableHead>
                            <TableHead>{t('seller.price')}</TableHead>
                            <TableHead>{t('seller.features')}</TableHead>
                            <TableHead>{t('common.actions')}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {items.map(item => (
                            <TableRow key={item.id}>
                              <TableCell className="font-medium">{item.name}</TableCell>
                              <TableCell className="text-sm text-muted-foreground max-w-xs truncate">
                                {item.description}
                              </TableCell>
                              <TableCell>{item.price} {item.currency}</TableCell>
                              <TableCell>
                                <div className="flex gap-1">
                                  {item.is_vegetarian && <Badge variant="outline" className="text-xs"><Leaf className="w-3 h-3" /></Badge>}
                                  {item.is_halal && <Badge variant="outline" className="text-xs">Helal</Badge>}
                                  {item.spicy_level > 0 && <Badge variant="outline" className="text-xs">🌶️{item.spicy_level}</Badge>}
                                </div>
                              </TableCell>
                              <TableCell>
                                <Button 
                                  variant="destructive" 
                                  size="sm"
                                  onClick={() => handleDeleteMenuItem(selectedRestaurant.id, item.id)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                );
              })}
              
              {(!selectedRestaurant.menu_items || selectedRestaurant.menu_items.length === 0) && (
                <Card className="p-8 text-center">
                  <p className="text-muted-foreground mb-4">{t('seller.noMenuItems')}</p>
                  <Button onClick={() => setShowAddMenuItem(true)}>
                    {t('seller.addFirstMenuItem')}
                  </Button>
                </Card>
              )}
            </div>
          )}
        </TabsContent>

        {/* Orders Tab */}
        <TabsContent value="orders" className="space-y-4">
          <h3 className="text-lg font-semibold">{t('seller.restaurantOrders')}</h3>
          
          {orders.length === 0 ? (
            <Card className="p-8 text-center">
              <ShoppingBag className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">{t('seller.noOrders')}</p>
            </Card>
          ) : (
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('seller.customer')}</TableHead>
                    <TableHead>{t('seller.orderType')}</TableHead>
                    <TableHead>{t('seller.items')}</TableHead>
                    <TableHead>{t('seller.total')}</TableHead>
                    <TableHead>{t('seller.status')}</TableHead>
                    <TableHead>{t('common.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orders.map(order => (
                    <TableRow key={order.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{order.customer_name}</p>
                          <p className="text-xs text-muted-foreground">{order.customer_phone}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{order.order_type}</Badge>
                      </TableCell>
                      <TableCell>
                        {order.items?.map((item, i) => (
                          <div key={i} className="text-sm">{item.quantity}x {item.name}</div>
                        ))}
                      </TableCell>
                      <TableCell className="font-bold">{order.total} TRY</TableCell>
                      <TableCell>
                        <Badge variant={
                          order.status === 'completed' ? 'default' :
                          order.status === 'cancelled' ? 'destructive' :
                          order.status === 'preparing' ? 'secondary' : 'outline'
                        }>
                          {order.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Select
                          value={order.status}
                          onValueChange={(v) => handleOrderStatusUpdate(order.id, v)}
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">{t('seller.pending')}</SelectItem>
                            <SelectItem value="confirmed">{t('seller.confirmed')}</SelectItem>
                            <SelectItem value="preparing">{t('seller.preparing')}</SelectItem>
                            <SelectItem value="ready">{t('seller.ready')}</SelectItem>
                            <SelectItem value="delivered">{t('seller.delivered')}</SelectItem>
                            <SelectItem value="completed">{t('seller.completed')}</SelectItem>
                            <SelectItem value="cancelled">{t('seller.cancelled')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Add Restaurant Dialog */}
      <Dialog open={showAddRestaurant} onOpenChange={setShowAddRestaurant}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('seller.addRestaurant')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('seller.restaurantName')} *</Label>
                <Input
                  value={newRestaurant.name}
                  onChange={(e) => setNewRestaurant({...newRestaurant, name: e.target.value})}
                  placeholder="Örn: Sultanahmet Kebapçısı"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('seller.cuisineType')}</Label>
                <Select 
                  value={newRestaurant.cuisine_type} 
                  onValueChange={(v) => setNewRestaurant({...newRestaurant, cuisine_type: v})}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {cuisineTypes.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>{t('seller.description')}</Label>
              <Textarea
                value={newRestaurant.description}
                onChange={(e) => setNewRestaurant({...newRestaurant, description: e.target.value})}
                placeholder={t('seller.restaurantDescPlaceholder')}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('seller.country')} *</Label>
                <Input
                  value={newRestaurant.country}
                  onChange={(e) => setNewRestaurant({...newRestaurant, country: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('seller.city')} *</Label>
                <Input
                  value={newRestaurant.city}
                  onChange={(e) => setNewRestaurant({...newRestaurant, city: e.target.value})}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>{t('seller.address')} *</Label>
              <Input
                value={newRestaurant.address}
                onChange={(e) => setNewRestaurant({...newRestaurant, address: e.target.value})}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('seller.phone')} *</Label>
                <Input
                  value={newRestaurant.phone}
                  onChange={(e) => setNewRestaurant({...newRestaurant, phone: e.target.value})}
                  placeholder="+90 5XX XXX XX XX"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('seller.email')}</Label>
                <Input
                  type="email"
                  value={newRestaurant.email}
                  onChange={(e) => setNewRestaurant({...newRestaurant, email: e.target.value})}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="flex items-center gap-2">
                <Switch
                  checked={newRestaurant.delivery_available}
                  onCheckedChange={(v) => setNewRestaurant({...newRestaurant, delivery_available: v})}
                />
                <Label>{t('restaurant.delivery')}</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={newRestaurant.takeaway_available}
                  onCheckedChange={(v) => setNewRestaurant({...newRestaurant, takeaway_available: v})}
                />
                <Label>{t('restaurant.takeaway')}</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={newRestaurant.dine_in_available}
                  onCheckedChange={(v) => setNewRestaurant({...newRestaurant, dine_in_available: v})}
                />
                <Label>{t('restaurant.dineIn')}</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddRestaurant(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleCreateRestaurant}>
              {t('seller.createRestaurant')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Menu Item Dialog */}
      <Dialog open={showAddMenuItem} onOpenChange={setShowAddMenuItem}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('seller.addMenuItem')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('seller.itemName')} *</Label>
                <Input
                  value={newMenuItem.name}
                  onChange={(e) => setNewMenuItem({...newMenuItem, name: e.target.value})}
                  placeholder="Örn: Adana Kebap"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('seller.category')} *</Label>
                <Select 
                  value={newMenuItem.category} 
                  onValueChange={(v) => setNewMenuItem({...newMenuItem, category: v})}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {menuCategories.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>{t('seller.description')}</Label>
              <Textarea
                value={newMenuItem.description}
                onChange={(e) => setNewMenuItem({...newMenuItem, description: e.target.value})}
                placeholder={t('seller.menuItemDescPlaceholder')}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('seller.price')} *</Label>
                <Input
                  type="number"
                  value={newMenuItem.price}
                  onChange={(e) => setNewMenuItem({...newMenuItem, price: parseFloat(e.target.value)})}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('seller.prepTime')} (dk)</Label>
                <Input
                  type="number"
                  value={newMenuItem.preparation_time}
                  onChange={(e) => setNewMenuItem({...newMenuItem, preparation_time: parseInt(e.target.value)})}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>{t('seller.imageUrl')}</Label>
              <Input
                value={newMenuItem.image_url}
                onChange={(e) => setNewMenuItem({...newMenuItem, image_url: e.target.value})}
                placeholder="https://..."
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                <Switch
                  checked={newMenuItem.is_vegetarian}
                  onCheckedChange={(v) => setNewMenuItem({...newMenuItem, is_vegetarian: v})}
                />
                <Label>{t('seller.vegetarian')}</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={newMenuItem.is_halal}
                  onCheckedChange={(v) => setNewMenuItem({...newMenuItem, is_halal: v})}
                />
                <Label>{t('seller.halal')}</Label>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>{t('seller.spicyLevel')}: {newMenuItem.spicy_level}</Label>
              <input
                type="range"
                min="0"
                max="3"
                value={newMenuItem.spicy_level}
                onChange={(e) => setNewMenuItem({...newMenuItem, spicy_level: parseInt(e.target.value)})}
                className="w-full"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddMenuItem(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleAddMenuItem}>
              {t('seller.addItem')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RestaurantTab;
